import React, { useMemo, useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import DownArrowIcon from 'icons/down-arrow.svg';
import InventoryIcon from 'icons/inventory.svg';
import SearchOffIcon from 'icons/search-off.svg';
import ThreeDotsIcon from 'icons/three-dots.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import voca from 'voca';

import {
  useCreateProjectArchiveMutation,
  useCreateProjectRestorationMutation,
} from 'components/hooks/projectMutations';
import { useProjectsDashboardsProjectsReportsDashboardReportQuery } from 'components/hooks/projectsDashboardsProjectsReportsDashboardReportQueries';
import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';

import TextButton from 'components/application/buttons/TextButton';
import Paginator from 'components/application/Paginator';
import SearchField from 'components/application/SearchField';
import SuccessToast from 'components/application/SuccessToast';
import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';
import UndoableToast from 'components/application/UndoableToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import LifecycleStatusIndicator from 'components/projects/components/LifecycleStatusIndicator';
import EmptyTableState from 'components/projectsDashboard/components/EmptyTableState';

import { formatDate } from 'components/helpers/dates';
import { formatData } from 'components/helpers/reactQuery';
import { pluralizeProjectsName } from 'components/helpers/resources/projects';
import { resourcesAsResourceMap } from 'components/helpers/serialisableResources';

export default function ArchivedProjectsIndex({
  defaultCurrentSearch,
  onCurrentSectionClick,
}) {
  const currentActor = useCurrentActor();

  const [currentSearch, setCurrentSearch, handleCurrentSearchInputChange] =
    useForm({ archiveSearch: defaultCurrentSearch || '' });

  const [debouncedCurrentSearch] = useDebounce(currentSearch, 250, {
    onChange: () => setCurrentPage(1),
  });

  const { folderPolicy, projectsRestorationPolicy } =
    useProjectsDashboardContext();

  const [currentPage, setCurrentPage] = useState(1);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.ArchivedProjectsIndex',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const dashboardReportParams = {
    filter: { archived: { eq: true } },
    page: currentPage,
    search: debouncedCurrentSearch.archiveSearch,
    selections: folderPolicy.canIndex ? ['folder', 'live'] : ['live'],
    sort: { column: 'archived_at', direction: 'applied' },
  };

  const {
    data: domainDashboardReport,
    isError: isDomainDashboardReportFetchError,
    isFetching: isDomainDashboardReportFetching,
    isLoading: isDomainDashboardReportLoading,
    isSuccess: isDomainDashboardReportFetchSuccess,
  } = useProjectsDashboardsProjectsReportsDashboardReportQuery({
    params: dashboardReportParams,
    queryOptions: {
      keepPreviousData: true,
      select: (data) =>
        formatData({ data, resourceTypes: ['folder', 'project'] }),
    },
  });

  const {
    account: {
      attributes: { projectReferenceLabel },
    },
    division: {
      attributes: { pluralRAMSName, singularRAMSName },
    },
  } = currentActor;

  const buildColumnsArgs = {
    canAction: projectsRestorationPolicy.canCreate,
    folderPolicy,
    projectReferenceLabel,
    singularRAMSName,
    translations: {
      headers: {
        archived: i18nTCommon('shared.archived'),
        folder: i18nTCommon('projects.folder'),
        live: i18nTCommon('shared.live'),
        reference: i18nTCommon('shared.reference'),
      },
      options: {
        restore: i18nTCommon('shared.restore'),
      },
    },
  };

  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    [JSON.stringify(buildColumnsArgs)],
  );

  const isDataPending =
    isDomainDashboardReportLoading ||
    isDomainDashboardReportFetching ||
    isDomainDashboardReportFetchError;

  const domainDashboardReportProjects = useMemo(() => {
    const projects =
      domainDashboardReport?.data.relationships.projects.data || [];

    return isDataPending ?
        Array(projects.length || 25).fill({})
      : projects.map(
          (project) => domainDashboardReport.included.project[project.id],
        );
  }, [isDataPending, domainDashboardReport]);

  const domainDashboardReportIncluded = useMemo(
    () => domainDashboardReport?.included || {},
    [domainDashboardReport],
  );

  const table = useReactTable({
    columns,
    data: domainDashboardReportProjects,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    meta: {
      included: domainDashboardReportIncluded,
      isDataPending,
    },
  });

  const isSearchFieldDisabled =
    isDomainDashboardReportFetchSuccess &&
    !debouncedCurrentSearch.archiveSearch &&
    domainDashboardReport.data.relationships.projects.data.length === 0;

  return (
    <div className='tw-flex tw-flex-col tw-gap-6'>
      <div className='tw-flex tw-flex-col tw-gap-1 tw-px-4'>
        <div className='tw-flex tw-h-10 tw-items-center tw-justify-between'>
          <div className='tw-flex tw-items-center tw-gap-1'>
            <InventoryIcon
              className='[&_path]:tw-fill-grey-500'
              height={20}
              width={20}
            />
            <p className='tw-mb-0 tw-px-1.5 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {i18nTCommon('shared.archive')}
            </p>
          </div>
        </div>
        <SearchField
          additionalClasses={classNames(
            'tw-w-70',
            isSearchFieldDisabled ? 'tw-bg-grey-025' : 'tw-bg-white',
          )}
          isDisabled={isSearchFieldDisabled}
          name='archiveSearch'
          onChange={handleCurrentSearchInputChange}
          onReset={() => {
            setCurrentSearch({ ...currentSearch, archiveSearch: '' });
          }}
          placeholder={i18nT('search.placeholder')}
          value={currentSearch.archiveSearch}
        />
      </div>
      {(
        isDomainDashboardReportFetchSuccess &&
        !debouncedCurrentSearch.archiveSearch &&
        domainDashboardReport.data.relationships.projects.data.length === 0
      ) ?
        <EmptyTableState
          icon={InventoryIcon}
          title={i18nT('emptyState.title', {
            interpolation: { escapeValue: false },
            pluralRAMSName,
          })}
        >
          <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
            {i18nT('emptyState.description', {
              interpolation: { escapeValue: false },
              pluralRAMSName,
            })}
          </p>
        </EmptyTableState>
      : <div className='tw-flex tw-flex-col tw-gap-2'>
          <div className='tw-flex tw-h-8 tw-items-center tw-px-4'>
            <p className='tw-mb-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-700'>
              {pluralizeProjectsName({
                count:
                  isDomainDashboardReportFetchSuccess ?
                    domainDashboardReport.meta.scopedCount
                  : 0,
                currentActor,
                shouldCapitalize: true,
                shouldIncludeCount: true,
              })}
            </p>
          </div>
          {(
            isDataPending ||
            domainDashboardReport.data.relationships.projects.data.length > 0
          ) ?
            <>
              <table className='tw-table-fixed tw-border-separate tw-select-none tw-border-1 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-grey-100 tw-bg-white'>
                <thead className='tw-bg-grey-050'>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          className={header.column.columnDef.meta?.classNames}
                          key={header.id}
                        >
                          <div
                            className={classNames(
                              'tw-flex tw-h-10 tw-items-center tw-justify-start tw-truncate tw-p-2 tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900',
                              header.column.getIsFirstColumn() ?
                                'tw-pl-4 tw-pr-2'
                              : 'tw-px-2',
                            )}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      className='tw-group/row tw-bg-white hover:tw-bg-grey-025 has-[button:hover]:tw-bg-blue-025'
                      key={row.id}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          className={classNames(
                            'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                            cell.column.columnDef.meta?.classNames,
                          )}
                          key={cell.id}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {isDomainDashboardReportFetchSuccess &&
                domainDashboardReport.meta.totalPages > 1 && (
                  <div className='tw-mt-12 tw-text-center'>
                    <Paginator
                      currentPage={domainDashboardReport.meta.currentPage}
                      onClick={(event) =>
                        setCurrentPage(
                          Number(event.currentTarget.getAttribute('data-page')),
                        )
                      }
                      totalPages={domainDashboardReport.meta.totalPages}
                    />
                  </div>
                )}
            </>
          : <EmptyTableState
              icon={SearchOffIcon}
              title={i18nTCommon('projects.noProjectsFound', {
                interpolation: { escapeValue: false },
                pluralRAMSName,
              })}
            >
              <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                <Trans
                  components={{
                    button: (
                      <button
                        className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-m tw-font-medium tw-text-blue-500'
                        onClick={onCurrentSectionClick}
                      />
                    ),
                  }}
                  i18nKey='projectsDashboard.components.ArchivedProjectsIndex.noResults.description'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ pluralRAMSName, singularRAMSName }}
                />
              </p>
            </EmptyTableState>
          }
        </div>
      }
    </div>
  );
}

ArchivedProjectsIndex.propTypes = {
  defaultCurrentSearch: PropTypes.string,
  onCurrentSectionClick: PropTypes.func.isRequired,
};

function buildColumns({
  canAction,
  folderPolicy,
  projectReferenceLabel,
  singularRAMSName,
  translations,
}) {
  return [
    {
      id: 'live',
      header: translations.headers.live,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending)
          return (
            <div className='tw-pl-4 tw-pr-2'>
              <DotSkeleton />
            </div>
          );

        const status = _.camelCase(row.original.meta.calculatedLifecycleStatus);

        return (
          <div className='tw-flex tw-h-full tw-items-center tw-justify-center tw-pl-4 tw-pr-2'>
            <LifecycleStatusIndicator status={status} />
          </div>
        );
      },
      meta: { classNames: 'tw-w-12' },
    },
    {
      id: 'project',
      header: voca.capitalize(singularRAMSName),
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-900'>
            {row.original.attributes.name}
          </div>
        );
      },
      meta: { classNames: 'tw-max-w-[300px]' },
    },
    folderPolicy.canIndex && {
      id: 'folder',
      header: translations.headers.folder,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        const folder =
          table.options.meta.included.folder[
            row.original.relationships.folder?.data?.id
          ];

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {folder?.attributes.name}
          </div>
        );
      },
      meta: { classNames: 'tw-w-40' },
    },
    {
      id: 'reference',
      header: projectReferenceLabel || translations.headers.reference,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {row.original.attributes.reference}
          </div>
        );
      },
      meta: { classNames: 'tw-w-30' },
    },
    {
      id: 'archivedAt',
      header: () => (
        <div className='tw-flex tw-items-center tw-gap-1'>
          {translations.headers.archived}
          <DownArrowIcon
            className='[&_path]:tw-fill-grey-300'
            height={20}
            width={20}
          />
        </div>
      ),
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
            {formatDate({
              date: row.original.attributes.archivedAt,
              format: 'dd MMM yy',
            })}
          </div>
        );
      },
      meta: { classNames: 'tw-w-30' },
    },
    canAction && {
      id: 'actions',
      header: '',
      accessorFn: (project) => project.id,
      cell: ({ row }) => (
        <ArchivedProjectsTableRowActions
          projectId={row.original.id}
          translations={translations}
        />
      ),
      meta: { classNames: 'tw-min-w-32 tw-w-32' },
    },
  ].filter(Boolean);
}

function ArchivedProjectsTableRowActions({ projectId, translations }) {
  const breadBoard = useBreadBoard();

  const createProjectArchiveMutation = useCreateProjectArchiveMutation();

  const createProjectRestorationMutation =
    useCreateProjectRestorationMutation();

  const handleRestore = ({ id }) => {
    createProjectRestorationMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          const resourceMap = resourcesAsResourceMap({
            resources: data.included,
            resourceTypes: ['folder', 'project'],
          });

          const project =
            resourceMap.project[data.data.relationships.project.data.id];

          const folder =
            resourceMap.folder[project.relationships.folder.data?.id];

          breadBoard.addToast(
            <UndoableToast
              isUndoActionDisabled={createProjectArchiveMutation.isLoading}
              onBurnToast={breadBoard.handleBurnToast}
              onUndoAction={() => handleRestoreUndo({ id: project.id })}
              toastText={
                <Trans
                  i18nKey={`projectsDashboard.components.ArchivedProjectsIndex.handleRestore.success.${folder ? 'withFolder' : 'withoutFolder'}`}
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    folderName: folder?.attributes.name,
                    projectName: project.attributes.name,
                  }}
                />
              }
            />,
          );
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  };

  const handleRestoreUndo = ({ id }) => {
    createProjectArchiveMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          const resourceMap = resourcesAsResourceMap({
            resources: data.included,
            resourceTypes: ['project'],
          });

          const project =
            resourceMap.project[data.data.relationships.project.data.id];

          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  i18nKey='projectsDashboard.components.ArchivedProjectsIndex.handleRestoreUndo.success'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ projectName: project.attributes.name }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  };

  return (
    <div className='tw-flex tw-items-center tw-justify-center'>
      <ThreeDotsIcon
        className='group-hover/row:tw-hidden [&_path]:tw-fill-grey-500'
        width={12}
      />
      <TextButton
        className='tw-hidden hover:tw-bg-blue-050 group-hover/row:tw-inline-flex'
        color='blue'
        disabled={createProjectRestorationMutation.isLoading}
        onClick={() => handleRestore({ id: projectId })}
        size='sm'
      >
        {translations.options.restore}
      </TextButton>
    </div>
  );
}
